<script setup>
import {onMounted, reactive, ref, watch} from "vue"
import store from "../../store";
import FormAddress from "../form/form-address.vue";
import CardFields from "./card-fields.vue";
import helpers from "@/lib/helpers";

const emits = defineEmits(['created', 'push'])

const props = defineProps({
  order: Object
})

const card = ref()
const vault = ref(null)
const customer = ref()
const transaction = ref({type: store.state.settings.defaults.transaction_type[0].value})

const b_error = ref()
const b_mode = ref('Saved Cards')
const b_token = ref()
const b_selected_card = ref()

const fields = ['type', 'txn_id', 'txn_date', 'm_type', 'external_id', 'notes',
  'payer_address1', 'payer_address2', 'payer_city', 'payer_country', 'payer_name', 'payer_state', 'payer_zip',
  'amount', '_apply', '_save_to_vault'
]
const form = reactive(fields.reduce((acc, key) => {
  acc[key] = null;
  return acc;
}, {}))

form.type = 'M'

const state = ref({})

helpers.asyncAction('/api/transaction/token', 'GET', {quiet: true}).then(({token}) => b_token.value = token).catch((e) => b_error.value = e && e.message)

watch(b_mode, () => b_selected_card.value = null)

watch(props.order, (v) => {
  if (!v)
    return

  form.amount = v.balance
  form._apply = form.amount
  customer.value = v.customer

  form.payer_name = props.order.bill_name
  form.payer_address1 = props.order.bill_address1
  form.payer_address2 = props.order.bill_address2
  form.payer_city = props.order.bill_city
  form.payer_state = props.order.bill_state
  form.payer_zip = props.order.bill_zip
  form.payer_country = props.order.bill_country
}, {immediate: true})

watch(customer, (v) => {
  if (v && v.has_braintree_vault)
    helpers.asyncAction(v._links.vault_url).then(v => vault.value = v)
}, {immediate: true})

const process = async () => {
  const url = props.order ? props.order._links.transaction_create_url : {href: '/api/transaction', method: 'POST'}
  state.value = {}

  if (form.type === 'B') {
    if (!vault.value || b_mode.value === 'New Card') {
      if (!card.value) {
        helpers.alert('The card form is not ready yet.')
        return
      }

      try {
        form._nonce = await card.value.tokenize()
      } catch (e) {
        helpers.alert(e.message)
        return
      }
    } else {
      if (!b_selected_card.value) {
        helpers.alert('Select a saved card or enter a new card number')
        return
      }
      form._saved_card = b_selected_card.value.token
    }
  }

  try {
    const result = await helpers.asyncAction(url, {data: form})
    emits('created', result)
    helpers.toast('Transaction created')
  } catch (e) {
    if (e.exception === 'ValidationException' && e.fields)
      state.value = e.fields
  }
}
</script>

<template>
  <div>
    <modal-portal to="bottom">
      <modal-portal to="top">
        <modal-row v-if="!!customer" is-text label="Customer">
          {{ customer.gli }} - {{ customer.company }}
        </modal-row>
        <modal-row label="Type" :feedback="state.type">
          <b-select :options="store.state.settings.defaults.transaction_type" v-model="form.type"
                    :state="state.type | state" size="sm"/>
        </modal-row>

        <modal-row v-if="can('transactions.tags.write')" label="Tags">
          <div class="pt-3">
            <tag v-model="form.tags" tags-url="/api/transaction/tags"/>
          </div>
        </modal-row>
      </modal-portal>

      <b-row class="mb-2">
        <b-col cols="12" v-if="!can('transactions.apply')">
          <b-alert show variant="warning">Txn can be created by not applied, requires transactions.apply.
          </b-alert>
        </b-col>

        <b-col :cols="order ? 6 : 12">
          <modal-row label="Amount" :feedback="state.amount" v-if="!['P','E','S'].includes(form.type)">
            <b-input-group size="sm" prepend="$">
              <form-currency :state="state.amount | state" v-model="form.amount"/>
            </b-input-group>
          </modal-row>
        </b-col>
        <b-col cols="6" v-if="!!order">
          <modal-row v-if="can('transactions.apply')" label="Apply" :feedback="state._apply">
            <b-input-group size="sm" prepend="$">
              <form-currency :state="state._apply | state" v-model="form._apply"/>
            </b-input-group>
          </modal-row>
        </b-col>

      </b-row>

      <div class="text-right">
        <b-btn size="sm" variant="outline-primary" class="px-5" @click="process">Create</b-btn>
      </div>
    </modal-portal>

    <modal-row label="Txn ID" :feedback="state.external_id" v-if="['P','E','S'].includes(form.type)">
      <b-form-input :state="state.external_id | state " v-model="form.external_id"/>
    </modal-row>

    <form-row label="Txn Date" :feedback="state.txn_date" v-if="['M','C'].includes(form.type)">
      <b-form-datepicker v-model="form.txn_date" :state="state.txn_date | state" size="sm"/>
    </form-row>

    <template v-if="form.type === 'M'">

      <form-row label="Sub Type" :feedback="state.m_type">
        <b-select :options="store.state.settings.defaults.transaction_manual_type" :state="state.m_type | state"
                  v-model="form.m_type"
                  size="sm"/>
      </form-row>
      <modal-row label="Check / Ref #" :feedback="state.external_id">
        <b-form-input :state="state.external_id | state " v-model="form.external_id"/>
      </modal-row>

      <form-divider/>
    </template>

    <div v-if="form.type === 'B'">
      <template v-if="b_token">
        <div v-if="b_error">
          <b-alert show variant="danger">{{ b_error }}</b-alert>
        </div>
        <template v-else>
          <modal-portal to="subtop" v-if="!!customer && (form.type === 'B')">
            <chip-selector :items="['Saved Cards', 'New Card']" v-model="b_mode"/>
          </modal-portal>
          <div v-if="!!vault && (b_mode === 'Saved Cards')">
            <b-list-group>
              <b-list-group-item v-for="card in vault.cards" :key="card.token" class="d-flex align-items-center"
                                 :active="b_selected_card === card" button @click="b_selected_card = card">
                <div>
                  <b-img :src="card.imageUrl"/>
                </div>
                <div class="ml-3 mr-auto h6">
                  <div>{{ card.maskedNumber }}</div>
                  <div>{{ card.expirationDate }}</div>
                </div>
                <div class="text-right">
                  <div class="text-muted">{{ card.createdAt.date | dateonly }}</div>
                </div>
              </b-list-group-item>
            </b-list-group>
            <form-divider/>
          </div>
          <div v-else-if="!vault || b_mode === 'New Card'">
            <card-fields ref="card" :token="b_token" @error="(m) => b_error = m"/>
            <div v-if="!!customer" class="pl-1 mb-3">
              <b-check switch v-model="form._save_to_vault">Save Card to Vault</b-check>
            </div>

            <modal-row label="Cardholder Name" :feedback="state.payer_name">
              <b-form-input :state="state.payer_name | state " v-model="form.payer_name"/>
            </modal-row>

            <modal-row label="Address">
              <form-address :state="state" v-model="form" objkey="payer_"/>
            </modal-row>

            <form-divider/>
          </div>
        </template>
      </template>
      <b-alert v-else show variant="warning">Braintree service not available</b-alert>
    </div>

    <modal-row label="Payor Name" :feedback="state.payer_name" v-if="['M','C'].includes(form.type)">
      <b-form-input :state="state.payer_name | state " v-model="form.payer_name"/>
    </modal-row>

    <modal-row label="Address" v-if="['M'].includes(form.type)">
      <form-address :state="state" v-model="form" objkey="payer_"/>
    </modal-row>

    <form-divider v-if="form.type === 'M'"/>

    <modal-row label="Notes" :feedback="state.notes">
      <b-form-input :state="state.notes | state " v-model="form.notes"/>
    </modal-row>
  </div>
</template>

<style scoped>

</style>